/**
 * In-memory + localStorage ring buffer of WhatsApp CTA watchdog violations.
 * Subscribes to `whatsapp-cta:violation` events dispatched by the watchdog
 * and exposes a tiny pub/sub API for the diagnostics panel.
 */

export interface CtaViolation {
  kind: string;
  detail: Record<string, unknown>;
  url: string;
  ts: number;
}

const STORAGE_KEY = "sg:whatsapp-cta:violations";
const MAX_ENTRIES = 200;

let store: CtaViolation[] = load();
const listeners = new Set<(v: CtaViolation[]) => void>();
let started = false;

function load(): CtaViolation[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(-MAX_ENTRIES) : [];
  } catch {
    return [];
  }
}

function persist() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* quota exceeded — ignore */
  }
}

function notify() {
  for (const fn of listeners) fn(store);
}

function handle(event: Event) {
  const ce = event as CustomEvent<CtaViolation>;
  if (!ce.detail) return;
  store = [...store, ce.detail].slice(-MAX_ENTRIES);
  persist();
  notify();
}

export function startWhatsAppCtaViolationStore(): void {
  if (started || typeof window === "undefined") return;
  started = true;
  window.addEventListener(
    "whatsapp-cta:violation",
    handle as EventListener,
  );
}

export function getCtaViolations(): CtaViolation[] {
  return store;
}

export function clearCtaViolations(): void {
  store = [];
  persist();
  notify();
}

export function subscribeCtaViolations(
  fn: (v: CtaViolation[]) => void,
): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
